import { lazy, Suspense } from 'react';
import NoSelection from 'shared/components/avatar/NoSelection';
import { cream } from 'shared/styles/colors';
import { Accessory05HighlightColors, AccessoryBaseColors, AccessoryAlt1BaseColors, AccessoryHighlightColorsAlt1 } from 'shared/types/colorSets';
import { AccessoriesBehindAllHairList, AccessoriesBehindEarList, AccessoriesBehindHeadList, MultiColorAccessoriesList, NoColorAccessoriesList } from 'shared/types/optionSets';
import { getHighlightColor } from '../utils/HighlightColors';
import { ACCESSORY_Z_INDEX, DEFAULT_THUMBNAIL_STYLE, EARS_Z_INDEX, getAvatarViewLayerStyle, HAIR_BACK_Z_INDEX, HEAD_Z_INDEX, STANDARD_LAYER_VIEWBOX } from '../utils/LayoutUtils';

interface Props {
  option: number;
  colorBase: string;
  isThumbnail?: boolean;
}

const THUMBNAIL_VIEWBOX = "100 100 850 850";

const AccessoryLayer = ({ option, colorBase, isThumbnail }: Props) => {
  const viewBox = isThumbnail ? THUMBNAIL_VIEWBOX : STANDARD_LAYER_VIEWBOX;

  let zIndex = ACCESSORY_Z_INDEX;

  if (AccessoriesBehindAllHairList.includes(option)) {
    zIndex = HAIR_BACK_Z_INDEX - 1;
  } else if (AccessoriesBehindHeadList.includes(option)) {
    zIndex = HEAD_Z_INDEX - 1;
  } else if (AccessoriesBehindEarList.includes(option)) {
    zIndex = EARS_Z_INDEX - 1;
  }

  const style = isThumbnail ? DEFAULT_THUMBNAIL_STYLE : getAvatarViewLayerStyle(zIndex);

  const componentName = option < 10 ? `Accessory0${option}` : `Accessory${option}`;
  const AccessoriesComponent = lazy(() => import(`shared/components/avatar/accessories/${componentName}`));

  // Currently only accessory options 1 and 5 have multiple colors
  const getAccessoryHighlight = () => {
    switch (option) {
      case 1:
        return getHighlightColor(colorBase, AccessoryAlt1BaseColors, AccessoryHighlightColorsAlt1);
      case 5:
        return getHighlightColor(colorBase, AccessoryBaseColors, Accessory05HighlightColors);
      default:
        // default case will not be used
        return cream;
    }
  }

  if (option === 0) {
    if (isThumbnail) {
      return (
        <NoSelection />
      );
    }
    return null;
  }

  return (
    <Suspense fallback={<div />}>
      {NoColorAccessoriesList.includes(option) && (
        <AccessoriesComponent
          viewBoxProp={viewBox}
          style={style}
        />
      )}
      {MultiColorAccessoriesList.includes(option) ? (
        <AccessoriesComponent
          viewBoxProp={viewBox}
          colorBase={colorBase}
          colorHighlight={getAccessoryHighlight()}
          style={style}
        />
      ) : (
        !NoColorAccessoriesList.includes(option) &&
        <AccessoriesComponent
          viewBoxProp={viewBox}
          colorBase={colorBase}
          style={style}
        />
      )}
    </Suspense>
  );
}

export default AccessoryLayer;
